import { RefObject, useEffect } from 'react'

import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'

// iOS Safari ignores `overflow: hidden` on body; pinning with `position: fixed` + restoring scrollY is the only reliable lock. Ref-counted so nested modals don't fight each other.
// Exception: on iOS Safari, `position: fixed` resets the browser's internal scroll-position tracking,
// causing the native Smart App Banner to reappear when a drawer opens. Use overflow:hidden on <html> instead (reliable on iOS 16+).
let lockCount = 0
let savedScrollY = 0
let savedDocHeight = 0
let savedPathname = ''
let savedBodyStyles: {
  position: string
  top: string
  left: string
  right: string
  width: string
} | null = null

// Evaluated once at module load (server-side navigator is undefined → false, which is correct).
const isIOSSafari =
  typeof navigator !== 'undefined' &&
  /iP(hone|od|ad)/.test(navigator.userAgent) &&
  !/CriOS|FxiOS/.test(navigator.userAgent)

function lockBody() {
  const body = document.body
  body.classList.add('overflowHidden')
  body.classList.remove('overflowUnset')

  if (lockCount === 0) {
    savedScrollY = window.scrollY
    savedDocHeight = document.documentElement.scrollHeight
    savedPathname = window.location.pathname
    savedBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    }

    if (isIOSSafari) {
      // position:fixed on body resets Safari's scroll-position tracking → native Smart App Banner
      // reappears (Safari interprets it as "scrolled to top"). Locking overflow on the root
      // element avoids that side-effect while still preventing body scroll on iOS 16+.
      document.documentElement.style.overflow = 'hidden' // eslint-disable-line i18next/no-literal-string
    } else {
      body.style.position = 'fixed'
      body.style.top = `-${savedScrollY}px`
      body.style.left = '0'
      body.style.right = '0'
      body.style.width = '100%'
    }
  }
  lockCount += 1
}

function unlockBody() {
  const body = document.body
  body.classList.add('overflowUnset')
  body.classList.remove('overflowHidden')

  lockCount = Math.max(0, lockCount - 1)
  if (lockCount > 0) return

  if (savedBodyStyles) {
    if (isIOSSafari) {
      document.documentElement.style.overflow = ''
    } else {
      body.style.position = savedBodyStyles.position
      body.style.top = savedBodyStyles.top
      body.style.left = savedBodyStyles.left
      body.style.right = savedBodyStyles.right
      body.style.width = savedBodyStyles.width
      // Only restore scroll when unlocking on the SAME page we locked on (e.g. closing the modal in
      // place). If the modal closed because we navigated away — the QATC "view full product" flow closes
      // via a pathname-change effect, so unlock runs after the route change — restoring savedScrollY would
      // scroll the freshly-loaded page (the PDP) to the previous page's (the PLP's) offset. STRFRNT-14520
      // added this restore for the in-place PDP image modal; gate it so it doesn't fire across navigation.
      if (window.location.pathname === savedPathname) {
        window.scrollTo(0, savedScrollY)
      } else {
        // Cross-navigation close (e.g. QATC "view full product details" → PDP): the freshly-loaded page is
        // at the top, but the legacy header hides on scroll-down via a persistent global flag that doesn't
        // reset across client navigation, and no scroll event fires here to clear it — so the navbar would
        // land stuck hidden. Reset it so the new page shows the header. [STRFRNT-15288]
        siteStateManager.setIsScrollingDown(false)
      }
    }
    savedBodyStyles = null
  }
}

/**
 * The user's real scroll position, even while a modal holds the body lock.
 *
 * The lock pins <body> with `position: fixed; top: -<savedScrollY>px`, so the browser reports
 * `window.scrollY === 0` for as long as it is on — the true position lives only in `savedScrollY`.
 * Anything that must read the position while a modal is open has to come through here. PDP scroll
 * restoration is the case that bit us: it saves on `routeChangeStart`, and the QATC "view full
 * product details" link only *schedules* the unlock (a React effect cleanup), so the lock is still
 * on when the save runs — a raw `window.scrollY` records 0 and Back then lands at the top.
 * [STRFRNT-15288]
 */
export const getUnlockedScrollY = (): number => (lockCount > 0 ? savedScrollY : window.scrollY)

/**
 * The document's real height, even while a modal holds the body lock.
 *
 * Same trap as `getUnlockedScrollY`, one property over: `position: fixed` takes <body> out of flow
 * and `overflow: hidden` clips it, so `document.documentElement.scrollHeight` collapses to exactly the
 * viewport while a modal is open — measured on pre-integ, 10088 -> 900. Anything deciding "was the
 * reader at the end of the page" during a locked navigation has to come through here, or every modal
 * exit looks like it happened at the bottom. [STRFRNT-15288]
 */
export const getUnlockedDocHeight = (): number =>
  lockCount > 0 ? savedDocHeight : document.documentElement.scrollHeight

const useLockBodyScroll = (freeze: boolean, contentRef?: RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!freeze) return

    lockBody()

    let preventScrollOutsideModal: ((e: TouchEvent) => void) | undefined
    if (contentRef) {
      preventScrollOutsideModal = (e: TouchEvent) => {
        if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
          e.preventDefault()
        }
      }
      document.addEventListener('touchmove', preventScrollOutsideModal, { passive: false })
    }

    return () => {
      unlockBody()
      if (preventScrollOutsideModal) {
        document.removeEventListener('touchmove', preventScrollOutsideModal)
      }
    }
  }, [freeze, contentRef])
}

export default useLockBodyScroll
